import { Box, ButtonBase, ButtonBaseProps, IconButton, Popover, PopoverProps, Stack, StackProps, styled } from "@mui/material";
import { alpha } from "@mui/material/styles";

export const AvatarWrapper = styled(Box)(({ theme }) => ({
  top: "50%",
  position: "absolute",
  left: "50%",
  translate: "-50% -50%",
  boxSizing: "border-box",
}));

export const HeaderBase = styled((props: StackProps) => <Stack direction="row" {...props} />)(({ theme }) => ({
  position: "fixed",
  zIndex: 2,
  width: "100vw",
  alignItems: "center",
  justifyContent: "space-between",
  boxSizing: "border-box",
  padding: theme.spacing(0, 4),
  isolation: "isolate",
  pointerEvents: "none",
  "& > *": {
    pointerEvents: "auto",
  },
}));

export const ProfileButton = styled((props: ButtonBaseProps) => <ButtonBase disableRipple {...props} />)(({ theme }) => ({
  position: "relative",
  height: theme.spacing(8),
  width: theme.spacing(8),
}));

export const HeaderButton = styled(IconButton)(({ theme }) => ({
  // Same background as HeaderTabBar
  backdropFilter: `blur(${theme.spacing(2)})`,
  backgroundColor: `${theme.palette.dsSurface.v300}`,
  padding: theme.spacing(1.5),
}));

export const TrainingPlanStatusButton = styled(HeaderButton)(({ theme }) => ({
  width: "auto",
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(10),
  color: theme.palette.dsBase.v200,
  transition: theme.transitions.create(["background-color", "transform"], {
    duration: theme.transitions.duration.shorter,
  }),
  "&:hover": {
    backgroundColor: theme.palette.dsSurface.v400,
  },
  "&:active": {
    backgroundColor: theme.palette.dsSurface.v500,
    transform: "scale(0.97)",
  },
  "&.Mui-focusVisible": {
    outline: `2px solid ${theme.palette.dsPrimary.v300}`,
    outlineOffset: theme.spacing(0.5),
  },
  "&.Mui-disabled": {
    color: theme.palette.dsNeutral.v300,
    filter: "grayscale(0.4)",
  },
}));

interface ITrainingPlanProgressSegmentProps {
  completed: boolean;
}

export const TrainingPlanProgressTrack = styled(Box)(({ theme }) => ({
  display: "flex",
  height: theme.spacing(1.25),
  gap: theme.spacing(0.75),
}));

export const TrainingPlanProgressSegment = styled(Box, {
  shouldForwardProp: prop => prop !== "completed",
})<ITrainingPlanProgressSegmentProps>(({ completed, theme }) => ({
  width: theme.spacing(5),
  height: "100%",
  flexShrink: 0,
  borderRadius: theme.spacing(1.25),
  backgroundColor: completed ? (theme.palette.dsGreen?.v200 ?? theme.palette.dsSuccess.v300) : alpha(theme.palette.dsBase.v200, 0.12),
  transition: theme.transitions.create("background-color", {
    duration: theme.transitions.duration.standard,
  }),
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
  },
}));

interface ITrainingPlanStreakIndicatorProps {
  achieved: boolean;
}

export const TrainingPlanStreakIndicator = styled(Box, {
  shouldForwardProp: prop => prop !== "achieved",
})<ITrainingPlanStreakIndicatorProps>(({ achieved, theme }) => ({
  display: "inline-flex",
  flexShrink: 0,
  alignItems: "center",
  gap: theme.spacing(0.5),
  color: achieved
    ? (theme.palette.dsAmber?.v100 ?? theme.palette.dsOrange?.v200 ?? theme.palette.dsPrimary.v300)
    : alpha(theme.palette.dsBase.v200, 0.32),
}));

export const BackButton = styled(IconButton)(({ theme }) => ({
  background: theme.palette.dsSurface.v400,
  padding: theme.spacing(1.5),
}));

export const HeaderPopover = styled((props: PopoverProps) => (
  <Popover
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "center",
    }}
    disableScrollLock
    {...props}
  />
))(({ theme }) => ({
  "& .MuiPopover-paper": {
    backgroundColor: theme.palette.dsBase.v100,
    borderRadius: theme.spacing(3),
  },
}));
